import { getToken } from './user-service';
import { SESSION_MAX_DURATION_MS } from '_utils/constants';

const SESSION_KEY = 'prm_session_id';
const LOGIN_TIME_KEY = 'prm_login_time';
const USER_KEY = 'prm_session_user';
const BASE = '/api/v1/mobileusers/sessions';


// Collapses concurrent open calls into a single POST. openPortalSession is async and the
// localStorage reuse-guard is only written after the POST resolves, so without this a single
// login that fires the effect twice (React double-invoke, two rapid userDb updates) would race
// two POSTs — both seeing no SESSION_KEY yet — and create two duplicate session docs with the
// same login minute. This is the source of the identical duplicate rows in the session report.
let openInFlight = null;

export const openPortalSession = async (body) => {
  // If an open call is already in progress, wait for it instead of starting a second one.
  if (openInFlight) return openInFlight;

  // Reuse an existing session only when it still belongs to the current user AND its
  // countdown has not elapsed. A stale anchor — browser closed without logout, a session
  // the server already expired, or a different controller on a shared workstation — is
  // discarded so we open a fresh session instead of counting down from someone else's
  // (or yesterday's) login. This is what stops the mid-shift "kicked after 3-4h" logouts.
  if (localStorage.getItem(SESSION_KEY)) {
    const sameUser = localStorage.getItem(USER_KEY) === body.userId;
    const remaining = getRemainingSessionMs(SESSION_MAX_DURATION_MS);
    if (sameUser && remaining !== null && remaining > 0) {
      return localStorage.getItem(SESSION_KEY);
    }
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LOGIN_TIME_KEY);
    localStorage.removeItem(USER_KEY);
  }

  openInFlight = (async () => {
    try {
      const token = await getToken();
      const res = await fetch(BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'firebase-uid': token },
        body: JSON.stringify({ ...body, type: 'portal' }),
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem(SESSION_KEY, data.id);
        // Anchor the absolute-timeout countdown to the server's authoritative loginTime,
        // not the client clock, so it can't drift from the record the cron uses.
        const loginMs = data.loginTime ? new Date(data.loginTime).getTime() : Date.now();
        localStorage.setItem(LOGIN_TIME_KEY, loginMs.toString());
        localStorage.setItem(USER_KEY, body.userId);
        return data.id;
      }
    } catch {
      // session tracking is non-fatal
    }
  })();

  try {
    return await openInFlight;
  } finally {
    openInFlight = null;
  }
};

// closedBy: 'user' | 'idle' | 'timeout'  (cron handles the rest server-side)
export const closePortalSession = async (closedBy = 'user') => {
  const sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) return;
  try {
    const token = await getToken();
    await fetch(`${BASE}/${encodeURIComponent(sessionId)}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'firebase-uid': token },
      body: JSON.stringify({ closedBy }),
    });
  } catch (e) {
    console.error('Error closing session', e);
    // non-fatal
  } finally {
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(LOGIN_TIME_KEY);
    localStorage.removeItem(USER_KEY);
  }
};

// Returns ms remaining before absolute session expiry, or null if no active session.
export const getRemainingSessionMs = (maxDurationMs) => {
  const loginTime = localStorage.getItem(LOGIN_TIME_KEY);
  if (!loginTime) return null;
  const elapsed = Date.now() - parseInt(loginTime, 10);
  return Math.max(0, maxDurationMs - elapsed);
};

// Returns { success, reason } from the server, or null on network error.
// Callers should check for { success: false, reason: 'already_closed' } and log the user out.
export const sendSessionHeartbeat = async () => {
  const sessionId = localStorage.getItem(SESSION_KEY);
  if (!sessionId) return null;
  try {
    const token = await getToken();
    const res = await fetch(`${BASE}/${encodeURIComponent(sessionId)}/heartbeat`, {
      method: 'PATCH',
      headers: { 'firebase-uid': token },
    });
    if (res.ok) return await res.json();
    return null;
  } catch {
    return null;
  }
};
